import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

import { useEffect, useState } from "react";
import { useGroupProvider } from "@/context/group-provider";
import ScreenRouteNames from "@/constants/screen-route-names";
import { getItem, setItem } from "@/lib/storage-utils";
import { STORAGE, STORAGE_KEY } from "@/constants";
import { useTheme } from "@/context/theme-provider";
import IDENTIFIERS from "@/constants/test_id";
import { LogCategories } from "@/services/logger/logger-types";

const Disclaimer = () => {
  const navigate = useNavigate();
  const { changeTheme } = useTheme();
  const { groupData } = useGroupProvider();

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  // 4.1.3 Status Messages — AA: the disclaimer text on screen has no announcement of its
  // own — a member who arrives here hears the "Disclaimer" heading and then nothing,
  // unless they navigate further to find the legal text. Populated a beat after mount
  // (not immediately) so it doesn't collide with the heading-focus announcement that
  // fires on route change (same class of bug as the earlier notification double-read).
  //
  // The announcement leads with "Disclaimer" itself rather than relying solely on the
  // separate heading-focus announcement to have already said it: that one is skipped on
  // the very first page a session lands on (arriving here via a fresh load or a direct
  // link, not a route change), which would otherwise leave this as unlabelled legal
  // prose with no context for what it is. This way the complete disclaimer — what it is
  // and its full text — is always announced as one self-contained unit.
  const [announcedDisclaimer, setAnnouncedDisclaimer] = useState("");

  // Sync theme from session storage (after loader sets PRODUCT_CONFIG)
  useEffect(() => {
    const productConfig = getItem(STORAGE_KEY.PRODUCT_CONFIG, STORAGE.SESSION);
    if (productConfig?.theme) {
      changeTheme(productConfig.theme);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!groupData?.disclaimer) return;
    const timer = setTimeout(
      () => setAnnouncedDisclaimer(`Disclaimer. ${groupData.disclaimer}`),
      250,
    );
    return () => clearTimeout(timer);
  }, [groupData?.disclaimer]);

  function updateCachedAgreement() {
    const cachedGroupDetail = getItem(
      STORAGE_KEY.CACHED_GROUP_DETAIL,
      STORAGE.LOCAL,
    );
    setItem(
      STORAGE_KEY.CACHED_GROUP_DETAIL,
      { ...cachedGroupDetail, agreement: true },
      STORAGE.LOCAL,
    );
  }

  function handleAgreement() {
    if (handleDisabled()) return;
    updateCachedAgreement();
    navigate(`/${ScreenRouteNames.LOGIN.path}`, { replace: true });
  }

  function openLink(url: string) {
    if (!url) return;
    window.open(url, "_blank");
  }

  function handleDisabled() {
    return Boolean(
      (groupData?.termsConditionsUrl && !termsAccepted) ||
      (groupData?.privacyPolicyUrl && !privacyAccepted),
    );
  }

  // 3.3.1 Error Identification — A: names the specific unticked agreement(s) rather
  // than a static "tick each agreement" line, so the description stays accurate as
  // the member ticks boxes one at a time.
  function describeMissingAgreements() {
    const missing = [
      groupData?.termsConditionsUrl && !termsAccepted
        ? "Terms & Conditions"
        : null,
      groupData?.privacyPolicyUrl && !privacyAccepted ? "Privacy Policy" : null,
    ].filter((item): item is string => Boolean(item));
    if (missing.length === 0) return "";
    const list =
      missing.length === 1 ? missing[0] : `${missing[0]} and ${missing[1]}`;
    return `Disabled. Tick ${list} to continue.`;
  }

  return (
    // 1.3.1 Info and Relationships — A / 2.4.1 Bypass Blocks — A: this page had no
    // <main> landmark, so it offered nothing for a skip link to target.
    <main className="flex h-full flex-col items-center justify-center space-y-4 px-10">
      <div className="h-16 max-w-40">
        {/* 1.1.1 Non-text Content — A: alt="customer logo" named the image's kind, not any
            information, and the element used to render with src={undefined} when no logo
            was supplied — a broken image announcing "customer logo". */}
        {Boolean(groupData?.client_logo) && (
          <img
            src={`data:image/png;base64,${groupData?.client_logo}`}
            alt=""
            className="h-full object-contain"
          />
        )}
      </div>
      <div className="place-items-center">
        {/* 1.3.1 — A: this was a <label> with no associated control. It is the page's
            title, so it is a heading. Classes are unchanged, so it looks identical. */}
        <h1
          className="my-6 overflow-hidden text-ellipsis text-center text-lg font-bold leading-none"
          data-testid={IDENTIFIERS.DISCLAIMER.TITLE}
        >
          Disclaimer
        </h1>
      </div>
      {/* 4.1.3 Status Messages — AA: mounted empty, so the delayed text change is what
          actually announces — a live region inserted already containing its message
          stays silent. */}
      <div role="status" aria-live="polite" className="sr-only">
        {announcedDisclaimer}
      </div>
      <div className="flex max-w-[400px] flex-col space-y-2">
        {/*
          1.3.1 — A: legal prose in a <label> with no control. Screen readers may treat
          it as a form label rather than body text. It is a paragraph.

          2.1.1 Keyboard — A / 2.4.3 Focus Order — A: a <p> is never a Tab stop, so a
          keyboard user tabbing through this page went straight from the heading to the
          first checkbox — the disclaimer text itself was unreachable by keyboard,
          reliant entirely on the timed live-region announcement above having already
          fired. tabIndex={0} puts it in the natural tab order, in its own reading
          position (before the checkboxes it explains), and — with no aria-label of its
          own — focusing it reads its full text content, the same way focusing any
          unlabelled container does. Unlike the one-shot announcement, this is
          re-visitable: Shift+Tab back to it any time.
        */}
        {/* 1.4.12 Text Spacing — AA: leading-none (line-height 1) made lines touch and
            clipped descenders, and overflow-hidden would cut off text for anyone applying
            their own spacing. Body-text leading, and nothing clipped. */}
        <p
          tabIndex={0}
          className="text-sm font-normal leading-relaxed text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          data-testid={IDENTIFIERS.DISCLAIMER.DISCLAIMER}
        >
          {groupData?.disclaimer}
        </p>
      </div>

      <div className="w-full">
        {Boolean(groupData?.termsConditionsUrl) && (
          <div className="flex items-center justify-start space-x-1">
            {/*
              4.1.2 Name, Role, Value — A / 3.3.2 Labels or Instructions — A. The checkbox's
              accessible name was just "I agree to", because the thing being agreed to lives
              in a separate control. A screen reader user heard "I agree to, checkbox" with
              no idea what they were consenting to — on a legal agreement. aria-labelledby
              stitches both visible strings together, so the name stays in sync with what is
              on screen ("I agree to Terms & Conditions").
            */}
            <Checkbox
              id="terms"
              checked={termsAccepted}
              onCheckedChange={() => setTermsAccepted(!termsAccepted)}
              aria-labelledby="terms-agree-label terms-link-label"
              data-testid={IDENTIFIERS.DISCLAIMER.TERMS_AND_CONDITIONS}
            />
            <Label
              id="terms-agree-label"
              htmlFor="terms"
              className="mb-0 flex items-center pl-2 text-xs"
            >
              I agree to{" "}
            </Label>
            <Button
              id="terms-link-label"
              className="px-2"
              variant="link"
              onClick={() => openLink(groupData?.termsConditionsUrl)}
              data-testid={IDENTIFIERS.DISCLAIMER.TERMS_AND_CONDITIONS_LINK}
              logCategory={LogCategories.TERMS_AND_CONDITION}
              logMetadata={{
                url: groupData.termsConditionsUrl,
              }}
            >
              Terms & Conditions
            </Button>
          </div>
        )}

        {Boolean(groupData?.privacyPolicyUrl) && (
          <div className="flex items-center justify-start space-x-1">
            {/* See the terms checkbox above — same 4.1.2 / 3.3.2 issue. */}
            <Checkbox
              id="privacy"
              checked={privacyAccepted}
              onCheckedChange={() => setPrivacyAccepted(!privacyAccepted)}
              aria-labelledby="privacy-agree-label privacy-link-label"
              data-testid={IDENTIFIERS.DISCLAIMER.PRIVACY_POLICY}
            />
            <Label
              id="privacy-agree-label"
              htmlFor="privacy"
              className="mb-0 flex items-center pl-2 text-xs"
            >
              I agree to{" "}
            </Label>
            <Button
              id="privacy-link-label"
              variant="link"
              className="px-2"
              onClick={() => openLink(groupData?.privacyPolicyUrl)}
              data-testid={IDENTIFIERS.DISCLAIMER.PRIVACY_POLICY_LINK}
              logCategory={LogCategories.PRIVACY_POLICY}
              logMetadata={{
                url: groupData.privacyPolicyUrl,
              }}
            >
              Privacy Policy
            </Button>
          </div>
        )}
      </div>
      <div>
        {/*
          3.3.1 Error Identification — A. Native `disabled` removed this from the tab
          order entirely — a keyboard user could never reach it to learn why. aria-disabled
          keeps it reachable (className reproduces the dimmed look), and the description
          names the specific unticked agreement(s). Because this button isn't gated by a
          form's own submit safety-net, the click handler itself re-checks handleDisabled()
          so it can't fire while an agreement is outstanding.
        */}
        <Button
          className={cn(
            "w-40 bg-primary hover:bg-primary-selected",
            handleDisabled() && "opacity-50",
          )}
          onClick={handleAgreement}
          aria-disabled={handleDisabled()}
          aria-describedby={handleDisabled() ? "agree-requirement" : undefined}
          data-testid={IDENTIFIERS.DISCLAIMER.AGREE_BUTTON}
        >
          I Agree
        </Button>
        {handleDisabled() && (
          <p id="agree-requirement" className="sr-only">
            {describeMissingAgreements()}
          </p>
        )}
      </div>
    </main>
  );
};

export default Disclaimer;
